/*
 * Per-office insurance portal credentials settings.
 *
 * Mental model surfaced up front: ONE default account per portal handles
 * breakdowns & eligibility; per-provider accounts handle estimates (a
 * predetermination only appears under the submitting dentist's account). The
 * page leads with the list of configured accounts; adding one happens in a
 * focused modal rather than an always-open form.
 *
 * Backed by:
 *   GET    /clinics/office/<id>/portal-credentials/
 *   POST   /clinics/office/<id>/portal-credentials/<portal_code>/        (create/update one account)
 *   DELETE /clinics/office/<id>/portal-credentials/<credential_id>/
 *   POST   /clinics/office/<id>/portal-credentials/<credential_id>/reset-2fa/
 *   POST   /clinics/office/<id>/portal-credentials/<credential_id>/set-default/
 */
import React, { useEffect, useState, useCallback } from "react";
import {
  Button,
  Dropdown,
  Empty,
  Input,
  Menu,
  Modal,
  Select,
  Switch,
  Tag,
  Tooltip,
  notification,
} from "antd";
import {
  PlusOutlined,
  ReloadOutlined,
  DeleteOutlined,
  MoreOutlined,
  SafetyCertificateOutlined,
  UserOutlined,
  ApiOutlined,
  LockOutlined,
  StarOutlined,
} from "@ant-design/icons";
import { controller } from "../../../controller";
import DashboardLayout from "../../../layout/dashboardLayout/DashboardLayout";
import "./portalCredentials.css";

const PORTAL_META = {
  SUNLIFE: { name: "SunLife", sub: "covers SunLife + CDCP" },
  PROVIDER_AGGREGATOR: { name: "ProviderConnect", sub: "GSC · MÉDIC · RBC · SSQ" },
};

function timeAgo(isoString) {
  if (!isoString) return null;
  const then = new Date(isoString).getTime();
  if (Number.isNaN(then)) return null;
  const diffSec = Math.floor((Date.now() - then) / 1000);
  if (diffSec < 60) return `${diffSec}s ago`;
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  return `${Math.floor(diffSec / 86400)}d ago`;
}

function initials(name) {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "—";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// ── Default-account details (the 6 office-level fields) ──────────────────────
// Required for whichever account is the office's default. Shared across portals
// (one set per office); the two office fields pre-fill from the Office record.
const PROFILE_FIELD_DEFS = [
  { key: "access_id", label: "Access ID", placeholder: "Portal access ID" },
  { key: "unique_id", label: "Unique ID", placeholder: "Portal unique ID" },
  { key: "doctor_name", label: "Doctor's Name", placeholder: "e.g. Dr. Jane Smith" },
  { key: "license_number", label: "License #", placeholder: "Dental license number" },
  { key: "office_contact_number", label: "Office Contact Number", placeholder: "e.g. 416-555-1000" },
  { key: "office_address", label: "Office Address", placeholder: "Street, city, province, postal code", multiline: true },
  // OPTIONAL, unlike the six above. Saved here so an estimate request can
  // pre-fill it instead of asking for the same number on every claim.
  {
    key: "cda_itrans_number",
    label: "CDA / iTrans #",
    placeholder: "Optional — pre-fills estimate requests",
    optional: true,
  },
];

const emptyProfile = () =>
  PROFILE_FIELD_DEFS.reduce((acc, f) => ({ ...acc, [f.key]: "" }), {});

// Copy just the 6 known keys out of a backend payload (which also carries a
// `complete` flag we don't edit).
const pickProfile = (src) => {
  const out = emptyProfile();
  if (src) PROFILE_FIELD_DEFS.forEach((f) => { out[f.key] = src[f.key] || ""; });
  return out;
};

// Optional fields are stored and sent, but never gate anything.
const REQUIRED_PROFILE_FIELDS = PROFILE_FIELD_DEFS.filter((f) => !f.optional);

const isProfileComplete = (p) =>
  REQUIRED_PROFILE_FIELDS.every((f) => String((p && p[f.key]) || "").trim());

const PROFILE_INCOMPLETE_MSG =
  "Access ID, Unique ID, Doctor's Name, License #, office contact number and address are all required for the default account.";

const InsuranceProfileFields = ({ profile, onChange }) => (
  <div className="opc-form-section">
    <div className="opc-section-head">Default account details</div>
    <div className="opc-field-hint" style={{ marginTop: -6, marginBottom: 12 }}>
      Saved once per office and used for the default account; contact &amp; address
      pre-fill from your office record. All required except where marked.
    </div>
    <div className="opc-grid2">
      {PROFILE_FIELD_DEFS.map((f) => {
        const val = (profile && profile[f.key]) || "";
        const set = (e) => onChange({ ...profile, [f.key]: e.target.value });
        return (
          <div className={`opc-field${f.multiline ? " opc-col-2" : ""}`} key={f.key}>
            <label className="opc-field-label">
              {f.label}
              {f.optional ? <span className="opc-field-optional"> (optional)</span> : null}
            </label>
            {f.multiline ? (
              <Input.TextArea value={val} onChange={set} placeholder={f.placeholder} autoSize={{ minRows: 2, maxRows: 4 }} />
            ) : (
              <Input value={val} onChange={set} placeholder={f.placeholder} autoComplete="off" />
            )}
          </div>
        );
      })}
    </div>
  </div>
);

// ── How-it-works explainer ──────────────────────────────────────────────────
const Explainer = () => (
  <div className="opc-explainer">
    <div className="opc-explainer-item">
      <div className="opc-explainer-icon">
        <SafetyCertificateOutlined />
      </div>
      <div>
        <div className="opc-explainer-title">Default account → breakdowns &amp; eligibility</div>
        <div className="opc-explainer-desc">
          Any account on the portal can pull a patient's coverage, so one account per portal is marked the default and
          used for all breakdowns &amp; eligibility checks.
        </div>
      </div>
    </div>
    <div className="opc-explainer-item">
      <div className="opc-explainer-icon">
        <UserOutlined />
      </div>
      <div>
        <div className="opc-explainer-title">Provider accounts → estimates</div>
        <div className="opc-explainer-desc">
          An estimate only appears under the account of the dentist who submitted it. Add one account per provider so
          estimates are fetched under the right one.
        </div>
      </div>
    </div>
  </div>
);

// ── One configured account ──────────────────────────────────────────────────
const AccountRow = ({ account, officeId, onChanged, profileComplete, onMakeDefault }) => {
  const [busy, setBusy] = useState(false);

  const hasProvider = !!account.provider;
  const name = hasProvider ? account.provider.name : "Office account";
  const avatarClass = account.is_default ? "is-default" : hasProvider ? "is-provider" : "is-office";
  const sessionAge = timeAgo(account.storage_state_saved_at);

  const run = async (fn, okMsg) => {
    setBusy(true);
    try {
      await fn();
      if (okMsg) notification.success({ message: okMsg });
      if (onChanged) await onChanged();
    } catch (err) {
      notification.error({ message: "Action failed", description: String(err.message || err) });
    } finally {
      setBusy(false);
    }
  };

  const confirmRemove = () =>
    Modal.confirm({
      title: "Remove this account?",
      content: `Deletes the saved credentials for ${name}. If it was the default, another account is promoted.`,
      okText: "Remove",
      okType: "danger",
      onOk: () => run(() => controller.deletePortalCredentials(officeId, account.id), "Account removed"),
    });

  // Legacy `overlay` + <Menu> is the proven click-dropdown pattern in this
  // codebase (React 16 / antd 5); the v5 `menu={{items}}` config object doesn't
  // render the popup here.
  const overlay = (
    <Menu>
      {!account.is_default && (
        <Menu.Item
          key="default"
          icon={<StarOutlined />}
          onClick={() => {
            // If the office already has a complete insurance profile, promote
            // directly. Otherwise open the modal to collect the required
            // default-account details first.
            if (profileComplete) {
              run(() => controller.setPortalCredentialDefault(officeId, account.id), `${name} is now the default`);
            } else if (onMakeDefault) {
              onMakeDefault(account);
            }
          }}
        >
          Make default
        </Menu.Item>
      )}
      <Menu.Item
        key="reset"
        icon={<ReloadOutlined />}
        onClick={() => run(() => controller.resetPortalCredential2FA(officeId, account.id), "2FA session cleared")}
      >
        Reset 2FA session
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item key="remove" danger icon={<DeleteOutlined />} onClick={confirmRemove}>
        Remove account
      </Menu.Item>
    </Menu>
  );

  return (
    <div className="opc-row">
      <div className={`opc-avatar ${avatarClass}`}>
        {hasProvider ? initials(name) : <SafetyCertificateOutlined />}
      </div>
      <div className="opc-row-main">
        <div className="opc-row-name">
          {name}
          {account.is_default && (
            <Tooltip title="Used for breakdowns & eligibility, and as the estimate fallback.">
              <span className="opc-pill">
                <StarOutlined style={{ fontSize: 10 }} /> Default
              </span>
            </Tooltip>
          )}
        </div>
        <div className="opc-row-meta">
          <span className={`opc-dot ${sessionAge ? "ok" : "warn"}`} />
          {sessionAge ? `2FA session active · saved ${sessionAge}` : "No 2FA session yet — next scrape will ask for a code"}
          {(account.preferred_2fa_destination || account.preferred_2fa_method) && (
            <>
              <span className="opc-meta-sep">·</span>
              2FA → {[account.preferred_2fa_method, account.preferred_2fa_destination].filter(Boolean).join(" · ")}
            </>
          )}
        </div>
      </div>
      <Dropdown overlay={overlay} trigger={["click"]} placement="bottomRight">
        <Button type="text" icon={<MoreOutlined />} loading={busy} />
      </Dropdown>
    </div>
  );
};

// ── Add / update account modal ──────────────────────────────────────────────
const AddAccountModal = ({ open, portalCode, providers, accounts, officeId, profilePrefill, onClose, onSaved }) => {
  const [providerId, setProviderId] = useState(undefined);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [preferred2fa, setPreferred2fa] = useState("");
  const [preferred2faMethod, setPreferred2faMethod] = useState("");
  const [isDefault, setIsDefault] = useState(false);
  const [profile, setProfile] = useState(emptyProfile());
  const [saving, setSaving] = useState(false);

  // Reset the form each time the modal opens. Default to "make default" when no
  // account exists yet (the first one has to be the default anyway). Pre-fill
  // the default-account details from the office's saved/on-file values.
  useEffect(() => {
    if (open) {
      setProviderId(undefined);
      setUsername("");
      setPassword("");
      setPreferred2fa("");
      setPreferred2faMethod("");
      setIsDefault(!(accounts && accounts.length));
      setProfile(pickProfile(profilePrefill));
    }
  }, [open, accounts, profilePrefill]);

  // Unmount entirely when closed. antd v5's Modal motion CSS isn't injecting in
  // this app, so antd's open→false hide waits on a transition that never fires
  // and the modal gets stuck open. Returning null guarantees it disappears the
  // instant `open` goes false (X / Cancel / mask-click / after save). Placed
  // after all hooks so the rules-of-hooks aren't violated.
  if (!open) return null;

  const meta = PORTAL_META[portalCode] || { name: portalCode };
  // The first account for a portal always becomes its default (the backend
  // auto-promotes it), so lock the toggle on and always collect the details.
  const isFirstAccount = !(accounts && accounts.length);
  const willBeDefault = isDefault || isFirstAccount;

  const handleSave = async () => {
    if (!username.trim() || !password) {
      notification.warning({ message: "Both username and password are required" });
      return;
    }
    // The default account requires the office-level details.
    if (willBeDefault && !isProfileComplete(profile)) {
      notification.warning({ message: "Fill in the default-account details", description: PROFILE_INCOMPLETE_MSG });
      return;
    }
    setSaving(true);
    try {
      await controller.savePortalCredentials(officeId, portalCode, {
        username: username.trim(),
        password,
        preferred_2fa_destination: preferred2fa.trim(),
        preferred_2fa_method: preferred2faMethod || null,
        // A default account is office-wide (the provider picker is hidden), so
        // never tie it to a dentist.
        provider_id: willBeDefault ? null : (providerId == null ? null : providerId),
        is_default: isDefault,
        insurance_profile: willBeDefault ? profile : undefined,
      });
      notification.success({
        message: `${meta.name} account saved`,
        description: "The stored 2FA session was reset; expect a 2FA prompt on the next scrape.",
      });
      onClose();
      if (onSaved) await onSaved();
    } catch (err) {
      notification.error({ message: "Save failed", description: String(err.message || err) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={`Add account — ${meta.name}`}
      okText="Save account"
      confirmLoading={saving}
      onOk={handleSave}
      destroyOnClose
      width={720}
      rootClassName="opc-modal-fix"
      zIndex={1050}
    >
      <div className="opc-default-toggle">
        <Switch checked={willBeDefault} disabled={isFirstAccount} onChange={setIsDefault} />
        <div>
          <div className="opc-field-label">Use as the default account</div>
          <div className="opc-field-hint" style={{ marginTop: 0 }}>
            {isFirstAccount
              ? "The first account for a portal is always its default — handles breakdowns & eligibility."
              : "Handles breakdowns & eligibility, and is the fallback for estimates. One default per portal."}
          </div>
        </div>
      </div>

      {/* Sign-in — provider (non-default only), then username + password side by side. */}
      <div className="opc-form-section">
        <div className="opc-section-head">Portal sign-in</div>

        {/* The default account is office-wide (breakdowns/eligibility for every
            patient), so it isn't tied to one dentist — hide the provider picker. */}
        {!willBeDefault && (
          <div className="opc-field" style={{ marginBottom: 14 }}>
            <label className="opc-field-label">Provider (dentist)</label>
            <Select
              allowClear
              showSearch
              optionFilterProp="children"
              style={{ width: "100%" }}
              placeholder="Pick the dentist this login belongs to"
              value={providerId}
              onChange={(v) => setProviderId(v)}
            >
              {providers.map((p) => (
                <Select.Option key={p.id} value={p.id}>
                  {p.name}
                </Select.Option>
              ))}
            </Select>
            <div className="opc-field-hint">Leave empty for a shared, office-wide account.</div>
          </div>
        )}

        <div className="opc-grid2">
          <div className="opc-field">
            <label className="opc-field-label">Username</label>
            <Input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="username" autoComplete="off" />
          </div>
          <div className="opc-field">
            <label className="opc-field-label">Password</label>
            <Input.Password
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="password"
              autoComplete="new-password"
            />
          </div>
        </div>
        <div className="opc-encrypt-note">
          <LockOutlined /> Encrypted at rest — saving resets the stored 2FA session.
        </div>
      </div>

      {/* Two-factor — method + destination side by side, one shared hint. */}
      <div className="opc-form-section">
        <div className="opc-section-head">Two-factor</div>
        <div className="opc-grid2">
          <div className="opc-field">
            <label className="opc-field-label">Method</label>
            <Select
              allowClear
              style={{ width: "100%" }}
              placeholder="How the code is sent"
              value={preferred2faMethod || undefined}
              onChange={(v) => setPreferred2faMethod(v || "")}
              options={[
                { value: "email", label: "Email" },
                { value: "text_message", label: "Text message" },
                { value: "phone_call", label: "Call" },
              ]}
            />
          </div>
          <div className="opc-field">
            <label className="opc-field-label">Send code to</label>
            <Input
              value={preferred2fa}
              onChange={(e) => setPreferred2fa(e.target.value)}
              placeholder="name@clinic.ca or a phone number"
              autoComplete="off"
            />
          </div>
        </div>
        <div className="opc-field-hint" style={{ marginTop: 6 }}>
          Only needed when the portal lists several emails/phones, or asks how to send the code.
        </div>
      </div>

      {willBeDefault && <InsuranceProfileFields profile={profile} onChange={setProfile} />}
    </Modal>
  );
};

// ── Set-as-default modal (collects the required default-account details) ──────
// Opened from an account's ⋯ → "Make default" when the office doesn't yet have a
// complete insurance profile on file.
const MakeDefaultModal = ({ account, profilePrefill, officeId, onClose, onSaved }) => {
  const [profile, setProfile] = useState(pickProfile(profilePrefill));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setProfile(pickProfile(profilePrefill));
  }, [profilePrefill, account]);

  if (!account) return null; // see AddAccountModal note: guarantees a clean hide.

  const name = account.provider ? account.provider.name : "Office account";

  const handleSave = async () => {
    if (!isProfileComplete(profile)) {
      notification.warning({ message: "Fill in the default-account details", description: PROFILE_INCOMPLETE_MSG });
      return;
    }
    setSaving(true);
    try {
      await controller.setPortalCredentialDefault(officeId, account.id, profile);
      notification.success({ message: `${name} is now the default` });
      onClose();
      if (onSaved) await onSaved();
    } catch (err) {
      notification.error({ message: "Couldn't set default", description: String(err.message || err) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={!!account}
      onCancel={onClose}
      title="Set as default account"
      okText="Make default"
      confirmLoading={saving}
      onOk={handleSave}
      destroyOnClose
      width={720}
      rootClassName="opc-modal-fix"
      zIndex={1050}
    >
      <p className="opc-field-hint" style={{ marginTop: 0 }}>
        <b>{name}</b> will handle breakdowns &amp; eligibility for this portal. Confirm the office details below — they're
        required for the default account.
      </p>
      <InsuranceProfileFields profile={profile} onChange={setProfile} />
    </Modal>
  );
};

// ── One portal section ──────────────────────────────────────────────────────
const PortalSection = ({ portalCode, accounts, providers, officeId, onChanged, onAdd, profileComplete, onMakeDefault }) => {
  const meta = PORTAL_META[portalCode] || { name: portalCode, sub: "" };
  const count = accounts.length;

  return (
    <div className="opc-portal">
      <div className="opc-portal-head">
        <div className="opc-portal-icon">
          <ApiOutlined />
        </div>
        <div>
          <div className="opc-portal-name">{meta.name}</div>
          {meta.sub && <div className="opc-portal-sub">{meta.sub}</div>}
        </div>
        <div className="opc-count">
          {count ? (
            <Tag color="purple" style={{ borderRadius: 999, marginInlineEnd: 0 }}>
              {count} account{count === 1 ? "" : "s"}
            </Tag>
          ) : (
            <Tag style={{ borderRadius: 999, marginInlineEnd: 0 }}>Not set up</Tag>
          )}
        </div>
      </div>

      <div className="opc-divider" />

      {count ? (
        accounts.map((account) => (
          <AccountRow
            key={account.id}
            account={account}
            officeId={officeId}
            onChanged={onChanged}
            profileComplete={profileComplete}
            onMakeDefault={onMakeDefault}
          />
        ))
      ) : (
        <div className="opc-empty">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No accounts yet — add one to fetch estimates under the right provider."
          />
        </div>
      )}

      <div className="opc-add" onClick={() => onAdd(portalCode)}>
        <PlusOutlined /> Add account
      </div>
    </div>
  );
};

// Carriers we fetch automatically (per-portal scraper accounts, handled above).
const SCRAPER_CARRIER_CODES = new Set(["SUNLIFE", "CDCP", "GSC", "CCQ", "RBC", "SSQ"]);

// ── One manual (non-scraper) carrier login ──────────────────────────────────
const ManualCredRow = ({ cred, officeId, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const name = (cred.insurance_provider && cred.insurance_provider.name) || "Carrier";

  const remove = () =>
    Modal.confirm({
      title: "Remove this login?",
      content: `Deletes the saved login for ${name}.`,
      okText: "Remove",
      okType: "danger",
      onOk: async () => {
        setBusy(true);
        try {
          await controller.deletePortalCredentials(officeId, cred.id);
          notification.success({ message: "Login removed" });
          if (onChanged) await onChanged();
        } catch (err) {
          notification.error({ message: "Delete failed", description: String(err.message || err) });
        } finally {
          setBusy(false);
        }
      },
    });

  return (
    <div className="opc-row">
      <div className="opc-avatar is-office">{initials(name)}</div>
      <div className="opc-row-main">
        <div className="opc-row-name">{name}</div>
        <div className="opc-row-meta">Manual login — your FTC team uses it to check coverage by hand</div>
      </div>
      <Button type="text" danger icon={<DeleteOutlined />} loading={busy} onClick={remove}>
        Remove
      </Button>
    </div>
  );
};

// ── Add a manual carrier login ───────────────────────────────────────────────
// Carriers sharing a portal_name share a login, so the picker offers portals
// with their carriers nested rather than a flat list of 14 insurers. Carriers
// with no portal_name fall back to their own name so nothing is hidden.
const groupCarriersByPortal = (carriers) => {
  const groups = new Map();
  (carriers || []).forEach((c) => {
    const key = c.portal_name || c.name;
    if (!groups.has(key)) groups.set(key, { portalName: key, portalUrl: c.portal_url, carriers: [] });
    groups.get(key).carriers.push(c);
  });
  return Array.from(groups.values()).sort((a, b) => a.portalName.localeCompare(b.portalName));
};

const ManualCarrierModal = ({ open, carriers, officeId, onClose, onSaved, preselectCarrierId }) => {
  const [carrierId, setCarrierId] = useState(undefined);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const selectedCarrier = (carriers || []).find((c) => c.id === carrierId);
  const selectedPortalUrl = (selectedCarrier && selectedCarrier.portal_url) || "";

  useEffect(() => {
    if (open) {
      // Opened from a carrier's own page, the carrier is not a question.
      setCarrierId(preselectCarrierId || undefined);
      setUsername("");
      setPassword("");
    }
  }, [open, preselectCarrierId]);

  if (!open) return null; // see AddAccountModal note: guarantees a clean hide.

  const handleSave = async () => {
    if (!carrierId) {
      notification.warning({ message: "Pick a carrier" });
      return;
    }
    if (!username.trim() || !password) {
      notification.warning({ message: "Both username and password are required" });
      return;
    }
    setSaving(true);
    try {
      await controller.saveManualCarrierCredential(officeId, carrierId, {
        username: username.trim(),
        password,
      });
      notification.success({ message: "Carrier login saved" });
      onClose();
      if (onSaved) await onSaved();
    } catch (err) {
      notification.error({ message: "Save failed", description: String(err.message || err) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title="Add a carrier login"
      okText="Save login"
      confirmLoading={saving}
      onOk={handleSave}
      destroyOnClose
      width={520}
      rootClassName="opc-modal-fix"
      zIndex={1050}
    >
      <div className="opc-field">
        <label className="opc-field-label">Portal &amp; carrier</label>
        <Select
          showSearch
          optionFilterProp="children"
          style={{ width: "100%" }}
          placeholder="Pick the portal you sign in to"
          value={carrierId}
          onChange={setCarrierId}
        >
          {/* Grouped by portal, because one login usually serves several
              carriers — an Instream account covers Manulife, Industrial
              Alliance and Johnston Group alike. */}
          {groupCarriersByPortal(carriers).map((group) => (
            <Select.OptGroup key={group.portalName} label={group.portalName}>
              {group.carriers.map((c) => (
                <Select.Option key={c.id} value={c.id}>
                  {c.name}
                </Select.Option>
              ))}
            </Select.OptGroup>
          ))}
        </Select>
        {selectedPortalUrl ? (
          <div className="opc-field-hint">
            Sign in at{" "}
            <a href={selectedPortalUrl} target="_blank" rel="noopener noreferrer">
              {selectedPortalUrl}
            </a>
          </div>
        ) : null}
        <div className="opc-field-hint">
          Carriers we don't fetch automatically. Your FTC team uses this login to check coverage on the carrier's portal.
        </div>
      </div>

      <div className="opc-field">
        <label className="opc-field-label">Portal username</label>
        <Input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="username" autoComplete="off" />
      </div>

      <div className="opc-field">
        <label className="opc-field-label">Portal password</label>
        <Input.Password
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="password"
          autoComplete="new-password"
        />
        <div className="opc-encrypt-note">
          <LockOutlined /> Encrypted at rest. Your FTC team can copy it while working a patient's file.
        </div>
      </div>
    </Modal>
  );
};

// ── Manual carriers section ──────────────────────────────────────────────────
const ManualCarriersSection = ({ manualCreds, officeId, onChanged, onAdd, focusCarrierName }) => {
  const count = manualCreds.length;
  return (
    <div className="opc-portal">
      <div className="opc-portal-head">
        <div className="opc-portal-icon">
          <ApiOutlined />
        </div>
        <div>
          <div className="opc-portal-name">
            {focusCarrierName ? `${focusCarrierName} (manual login)` : "Other carriers (manual login)"}
          </div>
          <div className="opc-portal-sub">
            {focusCarrierName
              ? "We don't fetch this carrier automatically — your FTC team logs in by hand"
              : "Carriers we don't fetch automatically — your FTC team logs in by hand"}
          </div>
        </div>
        <div className="opc-count">
          {count ? (
            <Tag color="purple" style={{ borderRadius: 999, marginInlineEnd: 0 }}>
              {count} login{count === 1 ? "" : "s"}
            </Tag>
          ) : (
            <Tag style={{ borderRadius: 999, marginInlineEnd: 0 }}>None</Tag>
          )}
        </div>
      </div>

      <div className="opc-divider" />

      {count ? (
        manualCreds.map((cred) => (
          <ManualCredRow key={cred.id} cred={cred} officeId={officeId} onChanged={onChanged} />
        ))
      ) : (
        <div className="opc-empty">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={focusCarrierName ? `No login saved for ${focusCarrierName} yet.` : "No manual carrier logins yet."}
          />
        </div>
      )}

      <div className="opc-add" onClick={onAdd}>
        <PlusOutlined /> Add carrier login
      </div>
    </div>
  );
};

const PortalCredentialsPanel = (props) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [providers, setProviders] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [modalPortal, setModalPortal] = useState(null);
  const [showManualModal, setShowManualModal] = useState(false);
  const [makeDefaultAccount, setMakeDefaultAccount] = useState(null);
  // `officeId`/`embedded` props let the onboarding wizard reuse this exact panel
  // (embedded, without the dashboard chrome); both default to the My Office
  // behaviour so that page is unchanged.
  const officeId = props.officeId || localStorage.getItem("selectedOffice");
  // `focusCarrier={{ id, code, name, portalCode }}` narrows the panel to a single
  // carrier for the Carrier Knowledge drawer. `portalCode` is handed down from the
  // server (portal_code_for_carrier) rather than re-derived here, so the mapping
  // lives in exactly one place. Absent → the full panel, unchanged.
  const focusCarrier = props.focusCarrier || null;

  const reload = useCallback(async () => {
    if (!officeId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const result = await controller.listPortalCredentials(officeId);
      setData(result);
    } catch (err) {
      notification.error({ message: "Failed to load portal credentials", description: String(err.message || err) });
    } finally {
      setLoading(false);
    }
  }, [officeId]);

  useEffect(() => {
    reload();
  }, [reload]);

  // Anything that CHANGED a credential also refreshes whatever embedded this
  // panel — the Carrier Knowledge coverage table would otherwise still claim the
  // office has no login a second after one was added. Deliberately not wired to
  // the mount load: that is not a change.
  const notifyChanged = props.onChanged;
  const reloadAndNotify = useCallback(async () => {
    await reload();
    if (notifyChanged) notifyChanged();
  }, [reload, notifyChanged]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await controller.getProviderList();
        const arr = Array.isArray(list) ? list : (list && list.results) || [];
        if (!cancelled) setProviders(arr);
      } catch (e) {
        // Non-fatal — provider dropdown just stays empty.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [officeId]);

  // Insurance carriers, for the "Other carriers (manual login)" section.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await controller.listInsuranceProviders();
        if (!cancelled) setCarriers((res && res.data) || []);
      } catch (e) {
        // Non-fatal — the manual-carrier picker just stays empty.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [officeId]);

  const byPortal = {};
  ((data && data.credentials) || []).forEach((c) => {
    byPortal[c.portal_code] = byPortal[c.portal_code] || [];
    byPortal[c.portal_code].push(c);
  });
  // Default account first, then by provider name.
  Object.values(byPortal).forEach((list) =>
    list.sort((a, b) => (b.is_default ? 1 : 0) - (a.is_default ? 1 : 0)),
  );
  const allPortals = (data && data.supported_portals) || ["SUNLIFE", "PROVIDER_AGGREGATOR"];
  // A portal serves several carriers, so focusing on one carrier means showing the
  // one portal it maps to — Sun Life and CDCP share a login, as do GSC/CCQ/RBC/SSQ.
  const portals = focusCarrier
    ? allPortals.filter((code) => code === focusCarrier.portalCode)
    : allPortals;
  // Shared office-level insurance profile (pre-filled from the office record).
  // `complete` gates whether "Make default" can promote directly or must first
  // collect these required fields.
  const insuranceProfile = (data && data.insurance_profile) || null;
  const profileComplete = !!(insuranceProfile && insuranceProfile.complete);

  // Manual (non-scraper) carrier logins: credential rows carrying an
  // insurance_provider. Their picker offers carriers that have a portal but no
  // AI scraper.
  const manualCreds = ((data && data.credentials) || [])
    .filter((c) => c.insurance_provider)
    .filter((c) => !focusCarrier || Number(c.insurance_provider.id) === Number(focusCarrier.id));
  const manualCarrierOptions = focusCarrier
    // Focused: offer this carrier and nothing else. The usual portal_url/scraper
    // filters exist to keep a 48-row picker sane; here the choice is already made,
    // and a carrier with no portal_url on file would otherwise be unaddable.
    ? (carriers || []).filter((c) => Number(c.id) === Number(focusCarrier.id))
    : (carriers || [])
        .filter((c) => c.portal_url && !SCRAPER_CARRIER_CODES.has(String(c.code || "").toUpperCase()))
        .sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
  // Manual logins are the alternative to an automated portal, so when a carrier has
  // one there is nothing manual to offer.
  const showManualSection = !focusCarrier || !focusCarrier.portalCode;

  const Wrapper = props.embedded ? React.Fragment : DashboardLayout;
  return (
    <Wrapper>
      <div className="opc-page">
        {/* The drawer already names the carrier and the office; repeating the page
            heading inside it would just push the accounts below the fold. */}
        {focusCarrier ? null : (
          <>
            <h1 className="opc-title">Insurance Portal Credentials</h1>
            <p className="opc-subtitle">
              Connect your clinic's logins for each insurance portal. Add an account per dentist so estimates are
              fetched under the right one, and mark a default for breakdowns &amp; eligibility. Portals without an
              account fall back to the platform-wide default.
            </p>
          </>
        )}

        {!officeId ? (
          <div style={{ marginTop: 32 }}>
            <Tag color="red">No office selected.</Tag>
          </div>
        ) : (
          <>
            {focusCarrier ? null : <Explainer />}
            {loading ? (
              <div style={{ textAlign: "center", padding: 40, color: "rgba(0,0,0,0.4)" }}>Loading…</div>
            ) : (
              <>
                {portals.map((portalCode) => (
                  <PortalSection
                    key={portalCode}
                    portalCode={portalCode}
                    accounts={byPortal[portalCode] || []}
                    providers={providers}
                    officeId={officeId}
                    onChanged={reloadAndNotify}
                    onAdd={setModalPortal}
                    profileComplete={profileComplete}
                    onMakeDefault={setMakeDefaultAccount}
                  />
                ))}
                {showManualSection ? (
                  <ManualCarriersSection
                    manualCreds={manualCreds}
                    officeId={officeId}
                    onChanged={reloadAndNotify}
                    onAdd={() => setShowManualModal(true)}
                    focusCarrierName={focusCarrier && focusCarrier.name}
                  />
                ) : null}
              </>
            )}
          </>
        )}

        <AddAccountModal
          open={!!modalPortal}
          portalCode={modalPortal}
          providers={providers}
          accounts={(modalPortal && byPortal[modalPortal]) || []}
          officeId={officeId}
          profilePrefill={insuranceProfile}
          onClose={() => setModalPortal(null)}
          onSaved={reloadAndNotify}
        />

        <MakeDefaultModal
          account={makeDefaultAccount}
          profilePrefill={insuranceProfile}
          officeId={officeId}
          onClose={() => setMakeDefaultAccount(null)}
          onSaved={reloadAndNotify}
        />

        <ManualCarrierModal
          open={showManualModal}
          preselectCarrierId={focusCarrier && focusCarrier.id}
          carriers={manualCarrierOptions}
          officeId={officeId}
          onClose={() => setShowManualModal(false)}
          onSaved={reloadAndNotify}
        />
      </div>
    </Wrapper>
  );
};

export default PortalCredentialsPanel;
